import React from 'react';
import { API } from '@/App';
import { UserRound, Phone, Cake } from 'lucide-react';
import { formatPhone } from '@/lib/phone';

// Shared Century Gothic stack used across the standardized personnel interfaces.
export const DIR_FONT = "'Century Gothic', 'Jost', 'Trebuchet MS', 'Segoe UI', sans-serif";

const fmtDob = (v) => {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : String(v);
};

const _CHIP_TONE = {
  Active: 'bg-[#8FC93A]/15 text-[#5FA718]', Approved: 'bg-blue-100 text-blue-700',
  'Pending Approval': 'bg-amber-100 text-amber-700', Draft: 'bg-slate-100 text-slate-600',
  'Returned for Correction': 'bg-amber-100 text-amber-700', Rejected: 'bg-rose-100 text-rose-600',
  Terminated: 'bg-rose-100 text-rose-600', Inactive: 'bg-slate-200 text-slate-500',
};
const StatusChip = ({ p }) => {
  if (p.pending_start) {
    return <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Pending Start</span>;
  }
  const label = p.lifecycle_status || (p.is_active !== false ? 'Active' : 'Inactive');
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${_CHIP_TONE[label] || 'bg-slate-200 text-slate-500'}`}>
      {label}
    </span>
  );
};

/**
 * Standardized personnel directory card (Administration + Operations).
 * Headshot ~30% larger than the legacy card; shows telephone and DOB (MM/DD/YY).
 * The entire card is clickable and opens the full-page Employee Profile Card.
 */
export const PersonnelDirectoryCard = ({ p, token, onOpen, footer }) => {
  const dob = fmtDob(p.date_of_birth);
  return (
    <div
      onClick={() => onOpen(p.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(p.id); } }}
      style={{ fontFamily: DIR_FONT }}
      className={`group cursor-pointer rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#1763B6] hover:shadow-md ${p.is_active === false ? 'bg-slate-50/60' : ''}`}
      data-testid={`personnel-card-${p.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-[84px] w-[84px] shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center" data-testid={`personnel-avatar-${p.id}`}>
          {p.photo_url ? (
            <img src={`${API}/personnel/${p.id}/photo?variant=thumb&auth=${token}&v=${(p.photo_url || '').slice(-8)}`} alt={p.name}
              className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : <UserRound size={34} className="text-slate-300" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate font-bold text-[#0E2A47] group-hover:underline" data-testid={`personnel-name-${p.id}`}>{p.name}</div>
            <StatusChip p={p} />
          </div>
          <div className="mt-0.5 font-mono text-xs font-bold text-slate-500" data-testid={`personnel-number-${p.id}`}>{p.personnel_number ? `#${p.personnel_number}` : (p.application_id || '—')}</div>
          <div className="mt-1 text-xs text-slate-600">{p.worker_classification || '—'}{p.primary_operational_role ? ` · ${p.primary_operational_role}` : ''}</div>
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={`personnel-phone-${p.id}`}>
            <Phone size={11} className="text-[#1763B6]" />{p.phone ? formatPhone(p.phone) : '—'}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={`personnel-dob-${p.id}`}>
            <Cake size={11} className="text-[#1763B6]" />{dob || '—'}
          </div>
          {p.has_login !== undefined && (
            <div className="mt-0.5 text-[11px] text-slate-400">{p.has_login ? 'Login linked' : 'No login'}</div>
          )}
        </div>
      </div>
      {footer && (
        <div className="mt-3 flex items-center justify-end gap-1 border-t border-slate-100 pt-2" onClick={(e) => e.stopPropagation()}>
          {footer}
        </div>
      )}
    </div>
  );
};

export default PersonnelDirectoryCard;
